import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MessageCircle, Trash2, Users, Check, X, AlertCircle } from "lucide-react";
import { format } from "date-fns";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useCircleBooking } from "@/hooks/useCircleBooking";
import LoadingSpinner from "@/components/ui/LoadingSpinner";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { formatPhoneForWhatsApp } from "@/utils/phone";

interface Attendee {
  id: string;
  user_id: string;
  quantity: number;
  credits_spent: number;
  booked_at: string;
  attendance_status?: "present" | "no_show" | "cancelled" | null | string;
  user?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    email: string | null;
    phone: string | null;
  };
}

type AttendanceStatus = "present" | "no_show" | "cancelled" | null;

interface CircleAttendeeManagerProps {
  eventId: string;
  attendees: Attendee[];
  capacity: number;
  eventStarted?: boolean;
}

export function CircleAttendeeManager({ 
  eventId, 
  attendees, 
  capacity, 
  eventStarted = false 
}: CircleAttendeeManagerProps) {
  const queryClient = useQueryClient();
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState<Attendee | null>(null);
  const { removeAttendee } = useCircleBooking();

  // Local attendance state - initialize from attendee data
  const [attendance, setAttendance] = useState<Record<string, AttendanceStatus>>(() => {
    const initial: Record<string, AttendanceStatus> = {};
    attendees.forEach((a) => {
      const status = a.attendance_status;
      // Cast string to AttendanceStatus only if it's a valid value
      if (status === "present" || status === "no_show" || status === "cancelled") {
        initial[a.id] = status;
      } else {
        initial[a.id] = null;
      }
    });
    return initial;
  });

  const totalBooked = attendees.reduce((sum, a) => sum + (a.quantity || 1), 0);

  // Check if there are unsaved attendance changes
  const hasAttendanceChanges = attendees.some((a) => {
    const originalStatus = a.attendance_status;
    const validOriginal = (originalStatus === "present" || originalStatus === "no_show" || originalStatus === "cancelled") 
      ? originalStatus 
      : null;
    return attendance[a.id] !== validOriginal;
  });

  // Save attendance mutation
  const saveAttendance = useMutation({
    mutationFn: async () => {
      const changedRecords = attendees
        .filter((a) => attendance[a.id] !== (a.attendance_status || null))
        .map((a) => ({
          bookingId: a.id,
          status: attendance[a.id],
        }));

      if (changedRecords.length === 0) return;

      const { data, error } = await supabase.functions.invoke("mark-circle-attendance", {
        body: { eventId, attendanceRecords: changedRecords },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["circle-event-bookings", eventId] });
      toast.success("Attendance saved");
    },
    onError: (error: Error) => {
      console.error("Error saving attendance:", error);
      toast.error(error.message || "Failed to save attendance");
    },
  });

  const setStatus = (bookingId: string, status: AttendanceStatus) => {
    setAttendance((prev) => ({
      ...prev,
      [bookingId]: prev[bookingId] === status ? null : status,
    }));
  };

  const handleRemoveClick = (attendee: Attendee) => {
    setSelectedAttendee(attendee);
    setRemoveDialogOpen(true);
  };

  const handleRemoveConfirm = async () => {
    if (!selectedAttendee) return;
    await removeAttendee.mutateAsync({ bookingId: selectedAttendee.id });
    setRemoveDialogOpen(false);
    setSelectedAttendee(null);
  };

  const getAttendeeName = (attendee: Attendee) => {
    if (!attendee.user) return "Unknown";
    const name = `${attendee.user.first_name || ""} ${attendee.user.last_name || ""}`.trim();
    return name || "Unknown";
  };

  const getInitials = (attendee: Attendee) => {
    if (!attendee.user) return "?";
    const first = attendee.user.first_name?.[0] || "";
    const last = attendee.user.last_name?.[0] || "";
    return (first + last).toUpperCase() || "?";
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5" />
                Manage Attendees
              </CardTitle>
              <CardDescription>
                {eventStarted 
                  ? "Contact attendees, mark attendance, or remove guests" 
                  : "View contact info and manage your guest list"}
              </CardDescription>
            </div>
            <span className="text-sm text-muted-foreground">
              {totalBooked}/{capacity} spots
            </span>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {attendees.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              No attendees yet
            </p>
          ) : (
            attendees.map((attendee) => {
              const currentStatus = attendance[attendee.id];
              return (
                <div
                  key={attendee.id}
                  className="flex items-start gap-3 p-3 rounded-lg border bg-card"
                >
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={attendee.user?.avatar_url || undefined} />
                    <AvatarFallback>{getInitials(attendee)}</AvatarFallback>
                  </Avatar>
                  
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">
                      {getAttendeeName(attendee)}
                    </p>
                    {attendee.user?.email && (
                      <p className="text-xs text-muted-foreground truncate">
                        {attendee.user.email}
                      </p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Booked {format(new Date(attendee.booked_at), "MMM d")}
                      {attendee.quantity > 1 && ` • +${attendee.quantity - 1} guest${attendee.quantity > 2 ? "s" : ""}`}
                    </p>
                  </div>

                  <div className="flex items-center gap-1">
                    {/* Attendance buttons - only show when event has started */}
                    {eventStarted && (
                      <>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8",
                            currentStatus === "present" && "bg-emerald-100 text-emerald-600 hover:bg-emerald-200 hover:text-emerald-700"
                          )}
                          onClick={() => setStatus(attendee.id, "present")}
                          title="Mark as present"
                        >
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8",
                            currentStatus === "no_show" && "bg-rose-100 text-rose-600 hover:bg-rose-200 hover:text-rose-700"
                          )}
                          onClick={() => setStatus(attendee.id, "no_show")}
                          title="Mark as no-show"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className={cn(
                            "h-8 w-8",
                            currentStatus === "cancelled" && "bg-amber-100 text-amber-600 hover:bg-amber-200 hover:text-amber-700"
                          )}
                          onClick={() => setStatus(attendee.id, "cancelled")}
                          title="Mark as cancelled"
                        >
                          <AlertCircle className="h-4 w-4" />
                        </Button>
                        <div className="w-px h-6 bg-border mx-1" />
                      </>
                    )}
                    
                    {/* WhatsApp button - direct chat with attendee */}
                    {attendee.user?.phone && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8"
                        asChild
                      >
                        <a 
                          href={`https://wa.me/${formatPhoneForWhatsApp(attendee.user.phone)}`}
                          target="_blank" 
                          rel="noopener noreferrer"
                          title={`Message ${getAttendeeName(attendee)} on WhatsApp`}
                        >
                          <MessageCircle className="h-4 w-4" />
                        </a>
                      </Button>
                    )}
                    
                    {/* Remove button */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                      onClick={() => handleRemoveClick(attendee)}
                      disabled={removeAttendee.isPending}
                      title="Remove attendee"
                    >
                      {removeAttendee.isPending && selectedAttendee?.id === attendee.id ? (
                        <LoadingSpinner />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              );
            })
          )}

          {/* Save attendance button - only show when there are changes */}
          {eventStarted && hasAttendanceChanges && (
            <Button
              onClick={() => saveAttendance.mutate()}
              disabled={saveAttendance.isPending}
              className="w-full mt-4 bg-violet-500 hover:bg-violet-600"
            >
              {saveAttendance.isPending ? (
                <>
                  <LoadingSpinner className="mr-2 h-4 w-4" />
                  Saving...
                </>
              ) : (
                "Save Attendance"
              )}
            </Button>
          )}
        </CardContent>
      </Card>

      <AlertDialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Attendee</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove{" "}
              <span className="font-medium">
                {selectedAttendee ? getAttendeeName(selectedAttendee) : "this attendee"}
              </span>{" "}
              from this event?
              {selectedAttendee && selectedAttendee.credits_spent > 0 && (
                <span className="block mt-2">
                  Their {selectedAttendee.credits_spent} credit{selectedAttendee.credits_spent !== 1 ? "s" : ""} will be refunded.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={removeAttendee.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRemoveConfirm}
              disabled={removeAttendee.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {removeAttendee.isPending ? (
                <LoadingSpinner className="mr-2 h-4 w-4" />
              ) : null}
              Remove & Refund
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
